import { isDataUIPart, isToolUIPart } from "ai";
import {
  type ChatAttachmentDataPart,
  isChatAttachmentDataPart,
} from "@/lib/ai/chat-attachments";
import type { AgentUIMessage } from "@/lib/ai/types";
import type {
  ActivityItem,
  ActivityRenderGroup,
  ActivityStep,
  MessageRenderGroup,
  RenderableToolUIPart,
  ToolRenderPlan,
} from "./render-model";

function isRenderableToolPart(
  part: AgentUIMessage["parts"][number],
): part is RenderableToolUIPart {
  return isToolUIPart(part as never);
}

function createActivityGroup(
  items: ActivityItem[],
  currentStepIndex: number,
): ActivityRenderGroup | null {
  if (items.length === 0) {
    return null;
  }

  const stepsByIndex = new Map<number, ActivityStep>();
  for (const item of items) {
    const step = stepsByIndex.get(item.stepIndex) ?? {
      stepIndex: item.stepIndex,
      items: [],
    };
    step.items.push(item);
    stepsByIndex.set(item.stepIndex, step);
  }

  // step-start 到着直後も新しい処理の待機状態を示せるよう、空の最新 step を境界として保持する。
  if (!stepsByIndex.has(currentStepIndex)) {
    stepsByIndex.set(currentStepIndex, {
      stepIndex: currentStepIndex,
      items: [],
    });
  }

  return {
    kind: "activity",
    steps: [...stepsByIndex.values()],
    toolCount: items.filter((item) => item.kind === "tool").length,
    hasReasoning: items.some((item) => item.kind === "reasoning"),
  };
}

function isTrailingAnswerMetadata(
  part: AgentUIMessage["parts"][number],
): boolean {
  return (
    part.type === "step-start" ||
    part.type === "source-url" ||
    part.type === "source-document" ||
    part.type === "file" ||
    isDataUIPart(part) ||
    (part.type === "reasoning" && part.text.trim().length === 0)
  );
}

function findAssistantAnswerTextIndexes(
  message: AgentUIMessage,
  toolPlans: ReadonlyMap<number, ToolRenderPlan>,
): ReadonlySet<number> {
  const answerTextIndexes = new Set<number>();

  const includeAnswerTextBefore = (endExclusive: number) => {
    let partIndex = endExclusive - 1;
    while (partIndex >= 0) {
      const part = message.parts[partIndex];
      if (!part) break;
      if (part.type === "text") {
        answerTextIndexes.add(partIndex);
        partIndex -= 1;
        continue;
      }
      if (isTrailingAnswerMetadata(part)) {
        partIndex -= 1;
        continue;
      }
      break;
    }
  };

  message.parts.forEach((part, partIndex) => {
    if (!isRenderableToolPart(part)) return;
    const placement = toolPlans.get(partIndex)?.placement;
    if (placement === "standalone" || placement === "activity-and-standalone") {
      includeAnswerTextBefore(partIndex);
    }
  });

  let partIndex = message.parts.length - 1;

  while (partIndex >= 0) {
    const part = message.parts[partIndex];
    if (!part) break;
    if (isTrailingAnswerMetadata(part)) {
      partIndex -= 1;
      continue;
    }
    if (isRenderableToolPart(part)) {
      const placement = toolPlans.get(partIndex)?.placement;
      if (
        placement === "standalone" ||
        placement === "activity-and-standalone"
      ) {
        partIndex -= 1;
        continue;
      }
    }
    break;
  }

  if (message.parts[partIndex]?.type === "text") {
    includeAnswerTextBefore(partIndex + 1);
  }
  return answerTextIndexes;
}

/**
 * UIMessage の時系列 part を、Activity / standalone / answer へ分類する。
 *
 * assistant の末尾に連続する text を最終回答とみなし、それ以前の text は
 * Activity 内の中間説明として保持する。standalone tool の直前にある text は、
 * tool ごとの前文として独立した answer group に残す。
 * step-start は描画せず、step の完了境界として保持する。
 */
export function groupMessageParts(
  message: AgentUIMessage,
  toolPlans: ReadonlyMap<number, ToolRenderPlan>,
): MessageRenderGroup[] {
  const assistantAnswerTextIndexes =
    message.role === "assistant"
      ? findAssistantAnswerTextIndexes(message, toolPlans)
      : null;

  const activityItems: ActivityItem[] = [];
  const standaloneGroups: Extract<
    MessageRenderGroup,
    { kind: "standalone" }
  >[] = [];
  const answerGroups: Extract<MessageRenderGroup, { kind: "answer" }>[] = [];
  const sourceParts: Extract<MessageRenderGroup, { kind: "sources" }>["parts"] =
    [];
  const fileParts: Extract<
    MessageRenderGroup,
    { kind: "attachments" }
  >["parts"] = [];
  const storedAttachments: Array<{
    part: ChatAttachmentDataPart;
    partIndex: number;
  }> = [];
  let stepIndex = 0;

  message.parts.forEach((part, partIndex) => {
    if (part.type === "step-start") {
      stepIndex += 1;
      return;
    }
    if (part.type === "reasoning" && message.role === "assistant") {
      // provider metadata だけを運ぶ空の reasoning part は永続化には残し、
      // Activity の空行としては表示しない。
      if (part.text.trim().length === 0) return;
      activityItems.push({ kind: "reasoning", part, partIndex, stepIndex });
      return;
    }
    if (part.type === "text") {
      if (
        message.role !== "assistant" ||
        assistantAnswerTextIndexes?.has(partIndex)
      ) {
        const previousAnswerGroup = answerGroups.at(-1);
        const previousAnswerPart = previousAnswerGroup?.parts.at(-1);
        if (
          previousAnswerGroup &&
          previousAnswerPart?.partIndex === partIndex - 1
        ) {
          previousAnswerGroup.parts.push({ part, partIndex });
        } else {
          answerGroups.push({
            kind: "answer",
            parts: [{ part, partIndex }],
          });
        }
      } else {
        activityItems.push({
          kind: "interim-text",
          part,
          partIndex,
          stepIndex,
        });
      }
      return;
    }
    if (part.type === "source-url" || part.type === "source-document") {
      sourceParts.push({ part, partIndex });
      return;
    }
    if (part.type === "file") {
      fileParts.push({ part, partIndex });
      return;
    }
    if (isChatAttachmentDataPart(part)) {
      storedAttachments.push({ part, partIndex });
      return;
    }
    if (isRenderableToolPart(part)) {
      const plan = toolPlans.get(partIndex);
      if (!plan) {
        return;
      }
      if (
        plan.placement === "standalone" ||
        plan.placement === "activity-and-standalone"
      ) {
        standaloneGroups.push({
          kind: "standalone",
          part,
          partIndex,
        });
      }
      if (
        plan.placement === "activity" ||
        plan.placement === "activity-and-standalone"
      ) {
        activityItems.push({
          kind: "tool",
          part,
          partIndex,
          stepIndex,
        });
      }
    }
  });

  const groups: MessageRenderGroup[] = [];
  const activity = createActivityGroup(activityItems, stepIndex);
  if (activity) groups.push(activity);
  const responseGroups: Array<
    Extract<MessageRenderGroup, { kind: "standalone" | "answer" }>
  > = [...standaloneGroups, ...answerGroups];
  // standalone の前文と、tool 後の最終回答の双方で元の時系列を保つ。
  responseGroups.sort((left, right) => {
    const leftIndex =
      left.kind === "answer" ? (left.parts[0]?.partIndex ?? 0) : left.partIndex;
    const rightIndex =
      right.kind === "answer"
        ? (right.parts[0]?.partIndex ?? 0)
        : right.partIndex;
    return leftIndex - rightIndex;
  });
  groups.push(...responseGroups);
  if (sourceParts.length > 0)
    groups.push({ kind: "sources", parts: sourceParts });
  if (fileParts.length > 0) {
    groups.push({ kind: "attachments", parts: fileParts });
  } else if (storedAttachments.length > 0) {
    groups.push({ kind: "attachments", parts: storedAttachments });
  }
  return groups;
}
